import {useState} from 'react'
import {Link} from '@mui/material'
import classnames from 'classnames'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'

import AddDeputyModal from '../../dashboard/src/components/corp_max_deputies/add/AddDeputyModal'
import DownloadIcon from '../../shared_components/DownloadIcon'
import FuneralHomeDetails from './FuneralHomeDetails'
import FuneralPackageDetails from './FuneralPackageDetails'
import PolicyInfo from './PolicyInfo'
import LargeModal from '../mui_base_components/modals/large_modal/LargeModal'
import ManagePermissionsContainer from '../../dashboard/src/components/permissions/specific_item/ManagePermissionsContainer'
import SuggestedDeputies from '../../dashboard/src/components/corp_max_deputies/SuggestedDeputies'
import {useOrganizationCobranding} from '../OrganizationCobrandingContext'
import PreviewIcon from '../svg_icons/icons/PreviewIcon'
import {hasOrganizationCobranding} from '../../lib/cobrandingTools'
import {addPossessive} from '../../lib/tools'
import {useDeputyData, useEverplanData, useVaultExtensionData} from '../../lib/hooks'

import './prearrangedFuneralModal.scss'

const PrearrangedFuneralModal = props => {
  const {
    clientName,
    everplanId,
    firm,
    firmConfig,
    onClose,
    onMemorialGuideClick,
    onPolicyDetailsClick,
    open,
    showClientText,
    userConfig = Map(),
    vaultExtension,
    vaultExtensionData,
    vaultExtensionPolicyInfo = List()
  } = props
  const [showAddDeputyModal, setShowAddDeputyModal] = useState(false)
  const [contactToAddAsDeputy, setContactToAddAsDeputy] = useState(Map())
  const [deputyToShareWith, setDeputyToShareWith] = useState(Map())

  const {deputies} = useDeputyData(everplanId)

  const {vaultExtension: groupedVaultExtension} = useVaultExtensionData(everplanId)

  const {itemResponses, newContacts} = useEverplanData(props.isOwnEverplan ? everplanId : null)

  const openAddDeputyModal = (contact = Map()) => {
    setContactToAddAsDeputy(contact)
    setShowAddDeputyModal(true)
  }

  const closeAddDeputyModal = () => {
    setContactToAddAsDeputy(Map())
    setShowAddDeputyModal(false)
  }

  const onToggleManagePermissionsModal = isOpen => {
    if (!isOpen)
      setDeputyToShareWith(Map())
  }

  const organizationCobranding = useOrganizationCobranding()

  const title = showClientText ?
    'My Prearrangements' :
    `${clientName ? `${addPossessive(clientName)} ` : ''}Prearrangements`

  return (
    <LargeModal
      className='prearranged-funeral-modal'
      onClose={onClose}
      open={open}>
      <div className='title'>{title}</div>
      <div
        className={classnames('package-name', {
          'with-everplans-custom-colors': !hasOrganizationCobranding(organizationCobranding)
        })}>
        {
          vaultExtensionData.get('package-name') ?
            vaultExtensionData.get('package-name') :
            title
        }
      </div>
      {
        props.isOwnEverplan && (
          <>
            <ManagePermissionsContainer
              deputyToShareWith={deputyToShareWith}
              isPrearrangementCard
              vaultExtension={vaultExtension}
              firmConfig={firmConfig}
              firstGroupId={vaultExtension.get('id')}
              userConfig={userConfig}
              keyField={vaultExtensionData.get('package-name', title)}
              onToggleManagePermissionsModal={onToggleManagePermissionsModal}
              setDeputyToShareWith={setDeputyToShareWith}
              openAddDeputyModal={openAddDeputyModal}
            />
            <SuggestedDeputies
              context='vault-extension'
              deputies={deputies}
              everplanId={everplanId}
              firstGroupId={vaultExtension.get('id')}
              numberToDisplay={2}
              onAddDeputy={openAddDeputyModal}
              onShareWithDeputy={setDeputyToShareWith}
              title={null}
              vaultExtension={vaultExtension}
            />
          </>
        )
      }
      {
        showClientText &&
        <div className='subtitle'>
          <span className='bold'>Congratulations!</span>
          <span>{' '}You've prearranged your funeral plan with{' '}</span>
          <span className='bold'>{firm.get('name')}</span>.
        </div>
      }
      <div className='action-buttons'>
        <Link className='download-link' onClick={onPolicyDetailsClick}>
          <PreviewIcon />
          Policy Details
        </Link>
        {
          // An owner downloads the guide to their own prearrangement, and a
          // firm user downloads it for the everplans they advise.
          (props.isOwnEverplan || (userConfig.get('firm-id') && !userConfig.get('client'))) && (
            <Link className='download-link memorial-guide' onClick={onMemorialGuideClick}>
              <DownloadIcon />
              Memorial Guide
            </Link>
          )
        }
      </div>
      <PolicyInfo policyInfo={vaultExtensionPolicyInfo} />
      <FuneralHomeDetails
        everplanId={everplanId}
        firm={firm.isEmpty() ? vaultExtension.get('firm', Map()) : firm}
        vaultExtensionData={vaultExtensionData}
      />
      <FuneralPackageDetails
        essentials={groupedVaultExtension.get('essentials', List())}
        merchandise={groupedVaultExtension.get('merchandise', List())}
        packageName={groupedVaultExtension.get('package-name')}
        services={groupedVaultExtension.get('services', List())}
      />
      {
        showAddDeputyModal && (
          <AddDeputyModal
            {...props}
            context='vault-extension'
            deputies={deputies}
            itemResponses={itemResponses}
            newContacts={newContacts}
            selectedContact={contactToAddAsDeputy}
            source={contactToAddAsDeputy.isEmpty() ? null : 'suggested'}
            exit={closeAddDeputyModal}
          />
        )
      }
    </LargeModal>
  )
}

PrearrangedFuneralModal.propTypes = {
  clientName: PropTypes.string,
  everplanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firm: PropTypes.instanceOf(Map),
  firmConfig: PropTypes.instanceOf(Map),
  isOwnEverplan: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onMemorialGuideClick: PropTypes.func.isRequired,
  onPolicyDetailsClick: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  params: PropTypes.object,
  showClientText: PropTypes.bool,
  userConfig: PropTypes.instanceOf(Map),
  vaultExtension: PropTypes.instanceOf(Map),
  vaultExtensionData: PropTypes.instanceOf(Map),
  vaultExtensionPolicyInfo: PropTypes.instanceOf(List)
}

export default PrearrangedFuneralModal
